import { Grid } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import type { Workflow, WorkflowProcess } from "@nens/rana/api-v1";
import { RanaAlert, RanaHeading, RanaMarkdown } from "@nens/ranalib";

import { BackButton } from "@/components/back-button";
import { useProcessListQuery } from "@/queries/query-process";

import { WorkflowProcessCard } from "./components/workflow-process-card";

interface WorkflowDetailProps {
  workflow: Workflow;
  onBack: () => void;
  onSelectProcess: (process: WorkflowProcess) => void;
  showProcessCards?: boolean;
  selectedProcessId?: string;
  children?: ReactNode;
}

export function WorkflowDetail({
  workflow,
  onBack,
  onSelectProcess,
  showProcessCards = true,
  selectedProcessId,
  children,
}: WorkflowDetailProps) {
  const { data: processList } = useProcessListQuery();
  const processDefinitions = processList?.items ?? [];
  const selectedProcessIndex = workflow.processes.findIndex(({ id }) => id === selectedProcessId);
  const nextProcess =
    selectedProcessIndex >= 0 ? workflow.processes[selectedProcessIndex + 1] : undefined;

  let processCards: ReactNode = null;
  if (showProcessCards) {
    processCards = workflow.processes.length ? (
      <Grid
        mt={4}
        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }}
        gap={3}
      >
        {workflow.processes.map((process) => (
          <WorkflowProcessCard
            key={process.id}
            process={process}
            processDefinition={processDefinitions.find(({ id }) => id === process.id)}
            onClick={() => onSelectProcess(process)}
          />
        ))}
      </Grid>
    ) : (
      <RanaAlert mt={4} status="error">
        <FormattedMessage
          id="workflow.no_processes"
          defaultMessage="This workflow has no available processes."
        />
      </RanaAlert>
    );
  }

  return (
    <>
      {showProcessCards && (
        <BackButton back={onBack}>
          <FormattedMessage id="workflow.back_to_workflows" defaultMessage="Workflows" />
        </BackButton>
      )}
      <RanaHeading type="h2" my={4}>
        {workflow.title}
      </RanaHeading>
      <RanaMarkdown readMoreHeight="md" markdown={workflow.description} />
      {processCards}
      {children}
      {nextProcess && (
        <>
          <RanaHeading mt={6} mb={4} type="h4">
            <FormattedMessage id="workflow.next_step" defaultMessage="Next workflow step" />
          </RanaHeading>
          <WorkflowProcessCard
            process={nextProcess}
            processDefinition={processDefinitions.find(({ id }) => id === nextProcess.id)}
            onClick={() => onSelectProcess(nextProcess)}
          />
        </>
      )}
    </>
  );
}
